import React, { memo, useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { useDomain } from '@techstyle/redux-core';

import { RootState } from '../../utils/types';
import usePrevious from '../../utils/usePrevious';

type CuralateWindow = Window &
  typeof globalThis & {
    crl8: {
      createExperience: (experienceName: string) => Promise<any>;
      destroyExperience: (experienceName: string) => Promise<any>;
      getExperience: (experienceName: string) => Promise<any>;
      ready: (callback: () => Promise<any>) => void;
    };
  };

const curalateWindow = () => {
  return window as CuralateWindow;
};

export const CurateFilterType = {
  LABEL: 'label',
  PRODUCT_ID: 'productId',
};

const Wrapper = styled.div<{ $isPdp: boolean }>`
  div[id*='crl8-custom'] * {
    ${({ $isPdp }) =>
      $isPdp &&
      css`
        ${({ theme }) => theme.header.variants.h3.textStyles}
        font-size: 20px !important;
        button {
          text-align: center;
          > div {
            ${({ theme }) => theme.links.variants.navigationSecondary}
            font-size: 14px !important;
            color: ${({ theme }) => theme.colors.grey} !important;
          }
        }
      `}
  }
`;

const Locales = {
  EN_US: 'en-US',
  EN_EU: 'en-EU',
};

const createExperience = async (
  experienceName: string,
  appendedErrorText: string = ''
) => {
  try {
    await curalateWindow().crl8.createExperience(experienceName);
  } catch (error) {
    Sentry.captureException(
      `${appendedErrorText}createExperience error: \n ${error}`
    );
  }
};

const destroyExperience = async (experienceName: string) => {
  try {
    await curalateWindow().crl8.destroyExperience(experienceName);
  } catch (error) {
    // No action is needed here as destroy experience is called
    // even if no experience exists
    // Error is just no experience returned
  }
};

const getAndDestroyExperience = async (
  experienceName: string,
  appendedErrorText: string = ''
) => {
  try {
    const exp = await curalateWindow().crl8.getExperience(experienceName);
    if (exp) {
      exp.destroy();
    }
  } catch (error) {
    Sentry.captureException(
      `${appendedErrorText}getAndDestroyExperience error: \n ${error}`
    );
  }
};

type CuralateExperienceProps = {
  destroyOnUnmount: boolean;
  experienceName: string;
  filterType: string;
  filterText: string;
  forceRefresh: boolean;
  isPdp: boolean;
};

const CuralateExperience = ({
  destroyOnUnmount = true,
  experienceName,
  filterType,
  filterText,
  forceRefresh = false,
  isPdp = false,
}: CuralateExperienceProps) => {
  const { tld } = useDomain();
  const locale = useSelector<RootState>(state => {
    return tld === '.eu' ? Locales.EN_EU : state.intl.locale || Locales.EN_US;
  });

  const filter = filterType && filterText ? `${filterType}:${filterText}` : '';
  const previousFilterType: string = usePrevious(filterType);
  const previousFilterText: string = usePrevious(filterText);
  const previousExperienceName = usePrevious(experienceName);

  useEffect(() => {
    if (curalateWindow().crl8) {
      curalateWindow().crl8.ready(async () => {
        await destroyExperience(experienceName);
        await createExperience(experienceName);
      });
    }

    return () => {
      if (destroyOnUnmount && curalateWindow().crl8) {
        curalateWindow().crl8.ready(async () => {
          await destroyExperience(experienceName);
        });
      }
    };
  }, [destroyOnUnmount, experienceName, forceRefresh]);

  useEffect(() => {
    const filterChanged: boolean =
      (previousFilterType !== filterType ||
        previousFilterText !== filterText) &&
      !!(previousFilterType || previousFilterText);

    if ((forceRefresh || filterChanged) && curalateWindow().crl8) {
      /**
       * For only _very specific_ color combinations, curalate was creating a new
       * experience before the last one was destroyed, which resulted in old query params
       * being passed to the new experience. This caused an "already been initialized"
       * error.
       * No idea why _specific_ color combinations were so consistent in triggering this
       * error.
       */
      curalateWindow().crl8.ready(async () => {
        if (
          forceRefresh &&
          previousExperienceName &&
          previousExperienceName !== experienceName
        ) {
          // just calling destroyExperience won't refresh the view instantly
          await getAndDestroyExperience(previousExperienceName);
        }
        await destroyExperience(experienceName);
        await createExperience(experienceName, 'didUpdate ');
      });
    }
  }, [
    experienceName,
    filterText,
    filterType,
    forceRefresh,
    previousFilterText,
    previousFilterType,
    previousExperienceName,
  ]);

  return (
    <Wrapper
      data-testid="curalate-experience"
      data-crl8-container-id={experienceName}
      data-crl8-auto-init="false"
      data-crl8-locale={locale}
      data-crl8-filter={filter}
      $isPdp={isPdp}
    />
  );
};

CuralateExperience.propTypes = {
  destroyOnUnmount: PropTypes.bool,
  experienceName: PropTypes.string.isRequired,
  filterText: PropTypes.string,
  filterType: PropTypes.string,
  forceRefresh: PropTypes.bool,
  isPdp: PropTypes.bool,
};
export default memo(CuralateExperience);
